import React, { useState } from 'react';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
} from '@mui/material';
import { useSetAtom } from 'jotai';
import {
  openAddInfoFormFiberSitePlanning,
  allSitesFiberSitePlanning,
} from '../../common/atom/atoms';
import { useBackdrop } from '../../common/components/backDrop_context.component';
import { notify } from '../../common/functions/react-toastify.function';
import { createSingleSiteRanDb } from '../RanDB/functions/create_single_site_ran_db.function';
import { useFiberDashboard } from './context/fiberDashboardContext';

export default function FiberSitePlanningAddSingleSiteComponent({ onSiteAdded }) {
  const setOpenAddInfoForm = useSetAtom(openAddInfoFormFiberSitePlanning);
  const setAllSites = useSetAtom(allSitesFiberSitePlanning);
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const { refreshDashboardData } = useFiberDashboard();

  const [formData, setFormData] = useState({
    site_code: '',
    project_name: '',
    status: 'In-Progress',
    onfp_planning_remarks: '',
    bp_remarks: '',
    comments: '',
    phase_name: '',
    bp: '',
    fiber_site_name: '',
    lat: '',
    long: '',
    technical_city: '',
    region: '',
    fiber_pop_id: '',
    ring_name: '',
    ring_unique_name: '',
    ring_name_nms: '',
    cutover_date: '',
    total_length: '',
    on_net: '',
    off_net: '',
    prefix: '',
    old_site_code: '',
    replacement_site_code: '',
    replacement_site_name: '',
    replacement_site_lat: '',
    replacement_site_long: '',
    replacement_site_proposed_ring: '',
    addendum_status: '',
    vendor: '',
    is_ftts: '',
    descope_status: '',
  });

  const handleCancel = () => {
    setOpenAddInfoForm(false);
    setFormData({
      site_code: '',
      project_name: '',
      status: 'In-Progress',
      onfp_planning_remarks: '',
      bp_remarks: '',
      comments: '',
      phase_name: '',
      bp: '',
      fiber_site_name: '',
      lat: '',
      long: '',
      technical_city: '',
      region: '',
      fiber_pop_id: '',
      ring_name: '',
      ring_unique_name: '',
      ring_name_nms: '',
      cutover_date: '',
      total_length: '',
      on_net: '',
      off_net: '',
      prefix: '',
      old_site_code: '',
      replacement_site_code: '',
      replacement_site_name: '',
      replacement_site_lat: '',
      replacement_site_long: '',
      replacement_site_proposed_ring: '',
      addendum_status: '',
      vendor: '',
      is_ftts: '',
      descope_status: '',
    });
  };

  const handleSubmit = async () => {
    // Add validation here
    if (!formData.site_code) {
      alert('Please fill in required fields');
      return;
    }

    showBackdrop();
    try {
      // Send entire form data as payload
      const payload = { ...formData };
      const resp = await createSingleSiteRanDb(payload, true);
      if (resp?.response) {
        notify('success', 'Saved Successfully');
        // Optimistically add to local list
        setAllSites((prevSites) => [
          ...prevSites,
          { ...payload, id: resp?.response?.id || Date.now() },
        ]);

        // Refresh dashboard data to get updated information
        try {
          await refreshDashboardData();
          console.log('Dashboard data refreshed after adding single site');

          // Call parent callback to refresh table data
          if (onSiteAdded && typeof onSiteAdded === 'function') {
            await onSiteAdded();
          }
        } catch (error) {
          console.error('Error refreshing dashboard data:', error);
          // Don't show error to user as the main operation was successful
        }

        setOpenAddInfoForm(false);
      }
    } catch (error) {
      console.error('Error adding fiber site:', error);
      notify('error', error?.message || 'Failed to add site');
    } finally {
      hideBackdrop();
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: 'white',
        boxShadow: '0px 0px 30px 2px rgba(0, 0, 0, 0.05)',
        borderRadius: 8,
        padding: 24,
        marginTop: '1%',
      }}
    >
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Site Code */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Site Code *"
              value={formData.site_code}
              onChange={(e) => handleChange('site_code', e.target.value)}
              required
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Project Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Project Name"
              value={formData.project_name}
              onChange={(e) => handleChange('project_name', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Status Dropdown */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleChange('status', e.target.value)}
                sx={{
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                }}
              >
                <MenuItem value="In-Progress">In Progress</MenuItem>
                <MenuItem value="On-Air">On Air</MenuItem>
                <MenuItem value="Planned">Planned</MenuItem>
                <MenuItem value="Non-Fiber">Non Fiber</MenuItem>
                <MenuItem value="Descoped">Descoped</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* ONFP Planning Remarks */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="ONFP Planning Remarks"
              value={formData.onfp_planning_remarks}
              onChange={(e) => handleChange('onfp_planning_remarks', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* BP Remarks */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="BP Remarks"
              value={formData.bp_remarks}
              onChange={(e) => handleChange('bp_remarks', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Comments */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Comments"
              value={formData.comments}
              onChange={(e) => handleChange('comments', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Phase Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phase Name"
              value={formData.phase_name}
              onChange={(e) => handleChange('phase_name', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* BP */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="BP"
              value={formData.bp}
              onChange={(e) => handleChange('bp', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Fiber Site Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Fiber Site Name"
              value={formData.fiber_site_name}
              onChange={(e) => handleChange('fiber_site_name', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Latitude */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Latitude"
              type="number"
              value={formData.lat}
              onChange={(e) => handleChange('lat', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Longitude */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Longitude"
              type="number"
              value={formData.long}
              onChange={(e) => handleChange('long', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Technical City */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Technical City"
              value={formData.technical_city}
              onChange={(e) => handleChange('technical_city', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Region */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Region"
              value={formData.region}
              onChange={(e) => handleChange('region', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Fiber POP ID */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Fiber POP ID"
              value={formData.fiber_pop_id}
              onChange={(e) => handleChange('fiber_pop_id', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Ring Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Ring Name"
              value={formData.ring_name}
              onChange={(e) => handleChange('ring_name', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Ring Unique Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Ring Unique Name"
              value={formData.ring_unique_name}
              onChange={(e) => handleChange('ring_unique_name', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Ring Name NMS */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Ring Name NMS"
              value={formData.ring_name_nms}
              onChange={(e) => handleChange('ring_name_nms', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Cutover Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Cutover Date"
              value={formData.cutover_date}
              onChange={(e) => handleChange('cutover_date', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Total Length */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Total Length"
              type="number"
              value={formData.total_length}
              onChange={(e) => handleChange('total_length', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* On Net */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="On Net"
              value={formData.on_net}
              onChange={(e) => handleChange('on_net', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Off Net */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Off Net"
              value={formData.off_net}
              onChange={(e) => handleChange('off_net', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Prefix */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Prefix"
              value={formData.prefix}
              onChange={(e) => handleChange('prefix', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Old Site Code */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Old Site Code"
              value={formData.old_site_code}
              onChange={(e) => handleChange('old_site_code', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Replacement Site Code */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Replacement Site Code"
              value={formData.replacement_site_code}
              onChange={(e) => handleChange('replacement_site_code', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Replacement Site Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Replacement Site Name"
              value={formData.replacement_site_name}
              onChange={(e) => handleChange('replacement_site_name', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Replacement Site Lat */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Replacement Site Latitude"
              type="number"
              value={formData.replacement_site_lat}
              onChange={(e) => handleChange('replacement_site_lat', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Replacement Site Long */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Replacement Site Longitude"
              type="number"
              value={formData.replacement_site_long}
              onChange={(e) => handleChange('replacement_site_long', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Replacement Site Proposed Ring */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Replacement Site Proposed Ring"
              value={formData.replacement_site_proposed_ring}
              onChange={(e) => handleChange('replacement_site_proposed_ring', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Addendum Status */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Addendum Status"
              value={formData.addendum_status}
              onChange={(e) => handleChange('addendum_status', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Vendor */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Vendor"
              value={formData.vendor}
              onChange={(e) => handleChange('vendor', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>

          {/* Is FTTS */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Is FTTS</InputLabel>
              <Select
                value={formData.is_ftts}
                label="Is FTTS"
                onChange={(e) => handleChange('is_ftts', e.target.value)}
                sx={{
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                }}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Descope Status */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Descope Status"
              value={formData.descope_status}
              onChange={(e) => handleChange('descope_status', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BD1D23',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#BD1D23',
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            borderColor: '#D0D5DD',
            color: '#667085',
            '&:hover': {
              borderColor: '#BD1D23',
              color: '#BD1D23',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            backgroundColor: '#BD1D23',
            '&:hover': {
              backgroundColor: '#A0181E',
            },
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Add Site
        </Button>
      </Box>
    </div>
  );
}
